/**
 * Juice state — ALL render-layer (docs/game-feel.md section E).
 *
 * Nothing in this file may touch RunState. Screenshake, hit-stop, slow-mo,
 * particles and camera zoom are properties of how a deterministic tick
 * stream is PRESENTED, never of the stream itself. The property test
 * `hash trail identical with juice on and off` is what keeps that true.
 *
 * Hit-stop deserves a specific note: it delays presentation, it does not
 * pause the sim's definition. The one legitimate freeze is the level-up
 * choice, and that one lives in the sim because it is in the input log.
 */
import juiceJson from '../../../config/juice.json';

export type JuiceConfig = typeof juiceJson;
export const juiceConfig: JuiceConfig = juiceJson;

/**
 * PUNCTUATION, NOT A MODE (founder, 2026-08-19, sim-wallclock-ratio).
 * `addHitStop` used to be re-triggerable on every qualifying hit with no
 * gate at all — under sustained fire against a horde, kills alone (let
 * alone ordinary hits) land far more often than `hitStop.lethalMs`
 * (80ms) takes to decay, and `Math.max` re-triggering means the timer
 * never actually reaches 0: measured live, `hitStopMs>0` on ~22% of
 * frames during a dense wave-10 kill burst, dragging the sim-to-
 * wallclock ratio to ~0.78 even for a build that already gated the
 * ORDINARY-hit case behind `heavy` damage (a first-pass fix that turned
 * out to leave kills, the actual dominant contributor at high kill
 * rate, untouched). A per-tier damage/lethality gate can't fix this by
 * itself: no matter which categories of hit are ALLOWED to trigger a
 * freeze, if that category's own EVENT RATE exceeds the freeze's own
 * duration, freezes chain back-to-back with no real gap. The fix has to
 * be a FREQUENCY cap, independent of category: a cooldown window after
 * each hit-stop ends during which a new one (other than a forced boss
 * beat) is refused outright, so hit-stop's worst-case ceiling on real
 * time is bounded (duration / (duration + cooldown)) regardless of how
 * many qualifying hits arrive. This is a render-feel tuning constant,
 * not sim balance -- kept as a plain constant here (like scene3d.ts's
 * camera constants) rather than in config/juice.json, since this turn's
 * DoD holds config/ diff empty.
 *
 * VALUE MEASURED, NOT GUESSED (`?bot=1`, in-page rAF sampling of
 * `state.tick` + `juice.hitStopMs` over a 3s window at the wave-10
 * checkpoint -- the densest of the three DoD waves, 106 enemies alive,
 * 1000+ kills already landed, no boss on screen to confound the reading
 * with the `force` bypass path):
 *   180ms  -> ratio 0.834, 31/181 frames frozen (barely moved the needle
 *             off the pre-cooldown 0.786 / 40 frames -- this window's
 *             qualifying-event rate was still high enough to refill the
 *             gate almost every time it opened)
 *   260ms  -> ratio 0.834, 31/181 (statistically the same -- confirms
 *             180-260ms wasn't yet in the range that mattered here)
 *   600ms  -> ratio 0.890, 20/181
 *   1000ms -> ratio 0.946, 10/181
 *   1500ms -> ratio 0.946, 10/181 (identical to 1000ms -- a plateau, the
 *             remaining ~5.5% is consistent with exactly ONE legitimate
 *             hit-stop firing during the 3s sample, i.e. punctuation
 *             working as intended, not a residual bug to chase further)
 * Shipped 1000ms: the shortest value that reached the plateau, so
 * hit-stop stays as responsive as the fix allows rather than picking a
 * bigger number "to be safe."
 */
const HIT_STOP_COOLDOWN_MS = 1000;

export class Juice {
  shake = 0;
  shakeX = 0;
  shakeY = 0;
  hitStopMs = 0;
  private hitStopCooldownMs = 0;
  timeScale = 1;
  private slowMoMs = 0;
  private slowMoTarget = 1;
  zoom: number;
  recoil = 0;
  vignette = 0;
  desaturate = 0;
  private lastHeartbeat = -1e9;
  reduceMotion = false;

  constructor(private readonly cfg: JuiceConfig) {
    this.zoom = cfg.camera.baseZoom;
    this.reduceMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  /** B4 — amplitude scaled to event magnitude, then capped. */
  addShake(amount: number): void {
    if (this.reduceMotion) return;
    this.shake = Math.min(this.cfg.screenshake.maxAmplitude, this.shake + amount);
  }

  /**
   * B2 — heavy hits only, or it becomes mush. Gated by
   * `HIT_STOP_COOLDOWN_MS` (see that constant's own comment): a new
   * hit-stop is refused while one is already running OR while in the
   * post-freeze cooldown, UNLESS `force` (boss hits only — the one
   * category rare enough by construction, exactly one boss on screen
   * ever, that it should always land regardless of what else just froze
   * the frame).
   */
  addHitStop(ms: number, force = false): void {
    if (!force && (this.hitStopMs > 0 || this.hitStopCooldownMs > 0)) return;
    this.hitStopMs = Math.max(this.hitStopMs, ms);
  }

  /** B6 / B7 — a ramp, not a hard pause. */
  addSlowMo(ms: number, scale: number): void {
    this.slowMoMs = Math.max(this.slowMoMs, ms);
    this.slowMoTarget = Math.min(this.slowMoTarget, scale);
  }

  /** B8 */
  updateNearDeath(hpFraction: number, nowMs: number, beat: () => void): void {
    const nd = this.cfg.nearDeath;
    if (hpFraction > 0 && hpFraction < nd.hpFraction) {
      const t = 1 - hpFraction / nd.hpFraction;
      this.vignette = nd.vignetteAlpha * t;
      this.desaturate = nd.desaturation * t;
      if (nowMs - this.lastHeartbeat > nd.heartbeatIntervalMs) {
        this.lastHeartbeat = nowMs;
        beat();
      }
    } else {
      this.vignette *= 0.9;
      this.desaturate *= 0.9;
    }
  }

  /** B9 — the arena opens up as the horde thickens. */
  updateZoom(enemyCount: number): void {
    const c = this.cfg.camera;
    const t = Math.min(1, enemyCount / c.densityForMinZoom);
    const target = c.baseZoom + (c.minZoom - c.baseZoom) * t;
    this.zoom += (target - this.zoom) * c.zoomLerp;
  }

  /** Advance render-side timers. Returns the sim time scale for this frame. */
  update(dtMs: number): number {
    const s = this.cfg.screenshake;
    if (this.shake > 0.01) {
      this.shake *= s.decayPerFrame;
      this.shakeX = (Math.random() * 2 - 1) * this.shake;
      this.shakeY = (Math.random() * 2 - 1) * this.shake;
    } else {
      this.shake = 0; this.shakeX = 0; this.shakeY = 0;
    }

    this.recoil *= this.cfg.recoil.decay;

    if (this.hitStopMs > 0) {
      this.hitStopMs -= dtMs;
      if (this.hitStopMs <= 0) this.hitStopCooldownMs = HIT_STOP_COOLDOWN_MS;
      return 0;
    }
    if (this.hitStopCooldownMs > 0) this.hitStopCooldownMs -= dtMs;
    if (this.slowMoMs > 0) {
      this.slowMoMs -= dtMs;
      this.timeScale += (this.slowMoTarget - this.timeScale) * 0.35;
      return this.timeScale;
    }
    this.slowMoTarget = 1;
    this.timeScale += (1 - this.timeScale) * 0.18;
    if (this.timeScale > 0.995) this.timeScale = 1;
    return this.timeScale;
  }
}
